/**
 * Вертикальный свайп между детентами нижней панели (спека 04): вверх —
 * раскрыть, вниз — свернуть и убрать совсем. Порог короткий: на ходу палец
 * ведёт неровно, требовать длинного жеста жестоко. Быстрый «флик» засчитывается
 * по скорости, даже если пройдено мало пикселей.
 *
 * Отдельно от use-drag.ts: там перетаскивание плавающего окна с запоминанием
 * позиции, здесь — дискретные состояния без сохранения.
 */
import { useRef } from 'preact/hooks'

/** Пройденное расстояние, после которого жест — свайп, а не тап. */
const SWIPE_MIN_PX = 28
/** Скорость «флика»: px/мс, при которой хватает и короткого движения. */
const FLICK_SPEED = 0.4
/** Горизонтальный увод, после которого жест считаем не нашим. */
const MAX_DRIFT_X = 60

export interface DetentSwipe {
  onPointerDown: (e: PointerEvent) => void
  /** true — последний жест был свайпом (гасить клик по панели). */
  wasSwipe: () => boolean
}

/** Подписка на свайп: up/down зовутся один раз за жест. */
export function useDetentSwipe(onUp: () => void, onDown: () => void): DetentSwipe {
  const swiped = useRef(false)

  const onPointerDown = (e: PointerEvent): void => {
    const startY = e.clientY
    const startX = e.clientX
    const startMs = performance.now()
    swiped.current = false
    let fired = false

    const finish = (dy: number, dx: number, dt: number): void => {
      if (fired || Math.abs(dx) > MAX_DRIFT_X) return
      const speed = Math.abs(dy) / Math.max(1, dt)
      if (Math.abs(dy) < SWIPE_MIN_PX && speed < FLICK_SPEED) return
      fired = true
      swiped.current = true
      if (dy < 0) onUp()
      else onDown()
    }

    const onMove = (ev: PointerEvent): void => {
      finish(ev.clientY - startY, ev.clientX - startX, performance.now() - startMs)
    }
    const onEnd = (ev: PointerEvent): void => {
      finish(ev.clientY - startY, ev.clientX - startX, performance.now() - startMs)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
  }

  return { onPointerDown, wasSwipe: () => swiped.current }
}

/**
 * Соседний детент в направлении жеста.
 *
 * `allowHidden` — можно ли уйти в «только ручка». Индустрия (Material 3
 * standard sheet, Apple Maps) держит панель ведения постоянной: терять путь
 * назад к маршруту нельзя, особенно в эвакуации. Прятать разрешаем ровно там,
 * где панель мешает главному — в полном AR, где манёвр и так на камере.
 */
export function nextDetent(
  current: 'hidden' | 'mini' | 'full',
  dir: 'up' | 'down',
  allowHidden = false,
): 'hidden' | 'mini' | 'full' {
  if (dir === 'up') return current === 'hidden' ? 'mini' : 'full'
  if (current === 'full') return 'mini'
  return allowHidden ? 'hidden' : 'mini'
}
